import User from 'domains/models/user';
import { schemaTeamItem } from '../team';
import { z } from 'zod';
import { RoleEnum } from '../team_user/enum';
import {
  PlanIdEnum,
  PlanVersionEnum,
  TrialChannelEnum,
} from '../subscription/enum';

export type UserUpdateBody = z.infer<typeof schemaUserUpdateBody>;
export const schemaUserUpdateBody = z.object({
  description: z.string().optional(),
  timeZone: z.string().optional(),
  locale: z.string().optional(),
  familyName: z.string().optional(),
  givenName: z.string().optional(),
  name: z.string().optional(),
  company: z.string().optional(),
  startWeekDay: z.number().optional(),
  isShowSameEventMerge: z.boolean().optional(),
  isShowEventOverlap: z.boolean().optional(),
  isShowDeclinedEvent: z.boolean().optional(),
  isShowWeekends: z.boolean().optional(),
  timeFormat: z.string().optional(),
  extraTimeZones: z.array(z.string()).optional(),
  calendarViewDays: z.number().optional(),
});

export type UserProfileItem = z.infer<typeof schemaUserProfileItem>;
export const schemaUserProfileItem = z.object({
  slug: z.string(),
  familyName: z.string().nullable(),
  givenName: z.string().nullable(),
  locale: z.string(),
  timeZone: z.string(),
  team: schemaTeamItem,
  teamUserRole: RoleEnum.optional(),
  color: z.string(),
  startWeekDay: z.number(),
  profile: z.string().nullable(),
  isShowSameEventMerge: z.boolean(),
  isShowEventOverlap: z.boolean(),
  isShowDeclinedEvent: z.boolean(),
  isShowWeekends: z.boolean(),
  calendarViewDays: z.number(),
  timeFormat: z.string(),
  extraTimeZones: z.array(z.string()).optional(),
});

export type ViewerTeamItem = z.infer<typeof schemaViewerTeamItem>;
export const schemaViewerTeamItem = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string().nullable(),
  profileImage: z.string().nullable(),
  stripeCustomerId: z.string(),
  slug: z.string(),
});

export type ViewerItem = z.infer<typeof schemaViewerItem>;
export const schemaViewerItem = z.object({
  uid: z.string(),
  name: z.string(),
  email: z.string(),
  locale: z.string(),
  profileImage: z.string().nullable(),
  role: RoleEnum,
  team: schemaViewerTeamItem.nullable(),
  planId: PlanIdEnum,
  planVersion: PlanVersionEnum,
  intercomUserHash: z.string().optional(),
  isTemporary: z.boolean(),
  isIpBanning: z.boolean(),
  isShowMessageFeature: z.boolean(),
  trialEndedAt: z.string().nullable(),
  trialChannel: TrialChannelEnum.nullable(),
  initialPlanChoseAt: z.string().nullable(),
  slug: z.string(),
  color: z.string().nullable(),
});

export type UserListItem = z.infer<typeof schemaUserListItem>;
export const schemaUserListItem = z.object({
  uid: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
});

// 公開情報のみを参照する用のインターフェース
export type SharedUserItem = z.infer<typeof schemaSharedUserItem>;
export const schemaSharedUserItem = z.object({
  uid: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  profile: z.string().nullable(),
  slug: z.string(),
});

// 公開情報のみを参照する用のインターフェース
export type SharedGuestUserItem = z.infer<typeof schemaSharedGuestUserItem>;
export const schemaSharedGuestUserItem = z.object({
  uid: z.string().nullable(),
  name: z.string(),
  profileImage: z.string().nullable(),
  color: z.string(), // profileImageがないユーザーの画像の背景色
});

export type UserItem = z.infer<typeof schemaUserItem>;
export const schemaUserItem = z.object({
  uid: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  email: z.string().nullable(),
});

export type LiveUser = z.infer<typeof schemaLiveUser>;
export const schemaLiveUser = z.object({
  uid: z.string(),
  name: z.string(),
  email: z.string(),
  profileImage: z.string().nullable(),
  display: z.string(),
  callUserId: z.string().nullable(),
  pageId: z.string().nullable(),
  color: z.string(),
});

export type UserScheduleCount = z.infer<typeof schemaUserScheduleCount>;
export const schemaUserScheduleCount = z.object({
  incoming: z.number(),
  pending: z.number(),
});

export const toListItem = (user: User): UserListItem => {
  return {
    uid: user.uid,
    name: user.name,
    profileImage: user.profileImage,
  };
};

export const toItem = (user: User): UserItem => {
  return {
    uid: user.uid,
    name: user.name,
    profileImage: user.profileImage,
    email: user.email,
  };
};
